import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { UserCheck, UserX } from 'lucide-react';

const fetchAllMedia = async () => {
  const response = await apiClient.get('/media/');
  return response.data.items;
};

const fetchPendingVerifications = async () => {
  const response = await apiClient.get('/auth/pending-verifications');
  return response.data;
};

const fetchUsers = async () => {
  const response = await apiClient.get('/auth/users');
  return response.data;
};

const handleVerification = async ({ userId, status }) => {
  const response = await apiClient.post('/auth/handle-verification', { userId, status });
  return response.data;
};

const setAnnotatorType = async ({ userId, annotatorType }) => {
  const response = await apiClient.post('/auth/set-annotator-type', { userId, annotatorType });
  return response.data;
};

const deleteMedia = async (id) => {
  const response = await apiClient.delete(`/media/${id}`);
  return response.data;
};

const bulkDeleteMedia = async (ids) => {
  const response = await apiClient.post('/media/bulk-delete', { ids });
  return response.data;
};

const bulkDeleteAnnotations = async (ids) => {
  const response = await apiClient.post('/media/bulk-delete-annotations', { ids });
  return response.data;
};

const getFilenameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition) return null;
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
  const filename = match?.[1] || match?.[2];
  return filename ? decodeURIComponent(filename) : null;
};

const downloadCsv = async ({ endpoint, fallbackFilename }) => {
  const response = await apiClient.get(endpoint, { responseType: 'blob' });
  const contentType = response.headers?.['content-type'] || 'text/csv';
  const contentDisposition = response.headers?.['content-disposition'];
  const filename = getFilenameFromContentDisposition(contentDisposition) || fallbackFilename;

  const blob = new Blob([response.data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const ManagePanel = () => {
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState('media');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState(null);
  const [isDownloadingSkips, setIsDownloadingSkips] = useState(false);

  const {
    data: items = [],
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ['allMedia'],
    queryFn: fetchAllMedia,
    enabled: activeSection === 'media'
  });

  const {
    data: pendingUsers = [],
    refetch: refetchUsers
  } = useQuery({
    queryKey: ['pendingUsers'],
    queryFn: fetchPendingVerifications,
    enabled: activeSection === 'users'
  });

  const {
    data: allUsers = [],
    refetch: refetchAllUsers
  } = useQuery({
    queryKey: ['allUsers'],
    queryFn: fetchUsers,
    enabled: activeSection === 'annotatorTypes'
  });

  const verifyMutation = useMutation({
    mutationFn: handleVerification,
    onSuccess: () => {
      refetchUsers();
      setMessage({ type: 'success', text: 'User status updated!' });
    },
    onError: () => {
      setMessage({ type: 'error', text: 'Failed to update user status' });
    }
  });

  const setTypeMutation = useMutation({
    mutationFn: setAnnotatorType,
    onSuccess: () => {
      refetchAllUsers();
      setMessage({ type: 'success', text: 'Annotator type updated!' });
    },
    onError: () => {
      setMessage({ type: 'error', text: 'Failed to update annotator type' });
    }
  });

  const filteredItems = items.filter(item => 
    item.originalName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const deleteMutation = useMutation({
    mutationFn: deleteMedia,
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Image deleted successfully!' });
      setSelectedImage(null);
      queryClient.invalidateQueries({ queryKey: ['allMedia'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Failed to delete image';
      setMessage({ type: 'error', text: errorMessage });
    }
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: bulkDeleteMedia,
    onSuccess: (data) => {
      setMessage({ type: 'success', text: data.message });
      setSelectedIds(new Set());
      setSelectedImage(null);
      queryClient.invalidateQueries({ queryKey: ['allMedia'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
    onError: (error) => {
      console.error('Bulk delete error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete images';
      setMessage({ type: 'error', text: errorMessage });
    }
  });

  const bulkDeleteAnnotationsMutation = useMutation({
    mutationFn: bulkDeleteAnnotations,
    onSuccess: (data) => {
      setMessage({ type: 'success', text: data.message });
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['allMedia'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
    onError: (error) => {
      console.error('Bulk delete annotations error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete annotations';
      setMessage({ type: 'error', text: errorMessage });
    }
  });

  const handleDelete = (item) => {
    if (window.confirm(`Are you sure you want to delete "${item.originalName}"? This will also delete all annotations for this image.`)) {
      deleteMutation.mutate(item.id);
    }
  };

  const handleToggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredItems.length && filteredItems.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredItems.map(item => item.id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    const idsArray = Array.from(selectedIds);
    console.log('Deleting IDs:', idsArray);
    if (window.confirm(`Are you sure you want to delete ${selectedIds.size} images? This cannot be undone.`)) {
      bulkDeleteMutation.mutate(idsArray);
    }
  };

  const handleBulkDeleteAnnotations = () => {
    if (selectedIds.size === 0) return;
    const idsArray = Array.from(selectedIds);
    console.log('Clearing annotations for IDs:', idsArray);
    if (window.confirm(`Are you sure you want to delete annotations for ${selectedIds.size} images? The images will remain.`)) {
      bulkDeleteAnnotationsMutation.mutate(idsArray);
    }
  };

  return (
    <section className="panel manage">
      <div className="manage-header">
        <div className="tabs">
          <button 
            className={`tab-btn ${activeSection === 'media' ? 'active' : ''}`}
            onClick={() => setActiveSection('media')}
          >
            Manage Media
          </button>
          <button 
            className={`tab-btn ${activeSection === 'users' ? 'active' : ''}`}
            onClick={() => setActiveSection('users')}
          >
            Pending Users
            {pendingUsers.length > 0 && <span className="badge">{pendingUsers.length}</span>}
          </button>
          <button 
            className={`tab-btn ${activeSection === 'annotatorTypes' ? 'active' : ''}`}
            onClick={() => setActiveSection('annotatorTypes')}
          >
            Annotator Types
          </button>
        </div>
      </div>

      {message && <p className={`message ${message.type}`}>{message.text}</p>}

      {activeSection === 'media' ? (
        <>
          <div className="manage-header-actions">
            <input 
              type="text" 
              placeholder="Search by name..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {selectedIds.size > 0 && (
              <>
                <button 
                  type="button" 
                  className="danger small" 
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteMutation.isPending}
                >
                  Delete {selectedIds.size}
                </button>
                <button 
                  type="button" 
                  className="warning small" 
                  onClick={handleBulkDeleteAnnotations}
                  disabled={bulkDeleteAnnotationsMutation.isPending}
                >
                  Clear Annotations ({selectedIds.size})
                </button>
              </>
            )}
            <button type="button" className="secondary" onClick={() => refetch()}>
              Refresh
            </button>

            <button
              type="button"
              className="secondary"
              onClick={async () => {
                try {
                  setIsDownloadingSkips(true);
                  await downloadCsv({ endpoint: '/media/export-skips-csv', fallbackFilename: 'skips.csv' });
                } catch (error) {
                  const errorMessage = error.response?.data?.message || 'Failed to download skips CSV';
                  setMessage({ type: 'error', text: errorMessage });
                } finally {
                  setIsDownloadingSkips(false);
                }
              }}
              disabled={isDownloadingSkips}
              title="Download per-user skips (unsure) as CSV"
            >
              {isDownloadingSkips ? 'Downloading…' : 'Download Skips CSV'}
            </button>
          </div>

          {isLoading && <p>Loading images…</p>}
          {isError && <p className="message error">Failed to load images.</p>}

          {!isLoading && !isError && items.length === 0 && (
            <p>No images uploaded yet. Go to Upload tab to add images.</p>
          )}

          {items.length > 0 && (
            <div className="manage-content">
              <aside className="manage-list">
                <div className="list-header">
                  <label className="select-all">
                    <input 
                      type="checkbox" 
                      checked={filteredItems.length > 0 && selectedIds.size === filteredItems.length}
                      onChange={handleSelectAll}
                    />
                    Select All ({filteredItems.length})
                  </label>
                </div>
                <ul>
                  {filteredItems.map((item) => (
                    <li key={item.id} className={selectedImage?.id === item.id ? 'active' : ''}>
                      <div className="list-item-row">
                        <input 
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={() => handleToggleSelect(item.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button
                          type="button"
                          className="item-button"
                          onClick={() => {
                            setSelectedImage(item);
                            setMessage(null);
                          }}
                        >
                          <span className="item-name">{item.originalName}</span>
                          <span className="item-meta">
                            <small>{item.annotationCount || 0} annotations</small>
                          </span>
                        </button>
                      </div>
                    </li>
                  ))}
                  {filteredItems.length === 0 && (
                    <li className="no-results">No images found matching "{searchTerm}"</li>
                  )}
                </ul>
              </aside>

              <div className="manage-viewer">
                {selectedImage ? (
                  <>
                    <div className="image-container">
                      <img src={selectedImage.url} alt={selectedImage.originalName} />
                    </div>
                    <div className="image-details">
                      <h3>{selectedImage.originalName}</h3>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <strong>Annotations:</strong>
                          <span>{selectedImage.annotationCount || 0}</span>
                        </div>
                        <div className="detail-item">
                          <strong>Size:</strong>
                          <span>{Math.round(selectedImage.size / 1024)} KB</span>
                        </div>
                        <div className="detail-item">
                          <strong>Type:</strong>
                          <span>{selectedImage.mimeType}</span>
                        </div>
                        <div className="detail-item">
                          <strong>Uploaded:</strong>
                          <span>{new Date(selectedImage.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      
                      {selectedImage.labelCounts && (
                        <div className="label-breakdown">
                          <h4>Label Breakdown</h4>
                          <div className="breakdown-grid">
                            <div className="breakdown-item">
                              <span className="label">Hate</span>
                              <span className="count">{selectedImage.labelCounts.hate || 0}</span>
                            </div>
                            <div className="breakdown-item">
                              <span className="label">Non-Hate</span>
                              <span className="count">{selectedImage.labelCounts['non-hate'] || 0}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="action-buttons">
                        <button
                          type="button"
                          className="danger"
                          onClick={() => handleDelete(selectedImage)}
                          disabled={deleteMutation.isPending}
                        >
                          {deleteMutation.isPending ? 'Deleting...' : 'Delete Image'}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="no-selection">
                    <p>Select an image from the list to view details</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      ) : activeSection === 'users' ? (
        <div className="users-list">
          <h3>Pending Verification Requests</h3>
          {pendingUsers.length === 0 ? (
            <p>No pending requests.</p>
          ) : (
            <div className="table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.map(user => (
                    <tr key={user._id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td className="actions-cell">
                        <button
                          className="icon-btn success"
                          onClick={() => verifyMutation.mutate({ userId: user._id, status: 'approved' })}
                          title="Approve"
                        >
                          <UserCheck size={20} />
                        </button>
                        <button
                          className="icon-btn danger"
                          onClick={() => verifyMutation.mutate({ userId: user._id, status: 'rejected' })}
                          title="Reject"
                        >
                          <UserX size={20} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="users-list">
          <h3>Annotator Types</h3>
          {allUsers.length === 0 ? (
            <p>No users found.</p>
          ) : (
            <div className="table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Verification</th>
                    <th>Annotator Type</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map((u) => (
                    <tr key={u._id}>
                      <td>{u.name}</td>
                      <td>{u.email}</td>
                      <td>{u.verificationStatus}</td>
                      <td>
                        <select
                          value={u.annotatorType || 'hate'}
                          onChange={(e) =>
                            setTypeMutation.mutate({ userId: u._id, annotatorType: e.target.value })
                          }
                          disabled={setTypeMutation.isPending}
                        >
                          <option value="hate">Hate annotator</option>
                          <option value="meme">Meme annotator</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ManagePanel;
